import { trackTealiumEvent, type TealiumDispatchStatus } from './client';
import {
  FANTASY_EVENT_CONTRACTS,
  FANTASY_GAME_ID,
  type FantasyEventContract,
  type FantasyEventContractKey,
} from './fantasyEventContracts';
import type { AnalyticsParams } from './types';

const INVALID_STRING_VALUES = new Set(['null', 'undefined', '[object object]']);
const UNRESOLVED_PLACEHOLDER = /\{[^}]+\}/;
const FORMATION_VALUE = /^\d{1,2}(?:-\d{1,2}){2,3}$/;

export type FantasyTrackingSkipReason =
  | 'unknown_contract'
  | 'invalid_details'
  | 'unapproved_field'
  | 'missing_required_field'
  | 'invalid_field_value'
  | 'unresolved_label';

export type FantasyPayloadBuildResult =
  | { ok: true; eventName: string; payload: AnalyticsParams }
  | { ok: false; reason: FantasyTrackingSkipReason; field?: string };

export type FantasyTrackingResult =
  | { status: TealiumDispatchStatus }
  | { status: 'skipped'; reason: FantasyTrackingSkipReason; field?: string };

type PlayerDetails = {
  player_id: string;
  player_name: string;
  player_team: string;
  player_position: string;
};

export interface FantasyEventDetailsByContract {
  onboardingFormationConfirmed: { formation: string };
  onboardingCaptainConfirmed: PlayerDetails & { captain_role: 'captain' };
  onboardingViceCaptainConfirmed: PlayerDetails & { captain_role: 'vice_captain' };
  onboardingTeamNameConfirmed: { team_name: string };
  transferAddPlayerCard: PlayerDetails;
  transferRemovePlayerCard: PlayerDetails;
  transferAddPlayerListing: PlayerDetails;
  transferRemovePlayerListing: PlayerDetails;
  transferConfirmed: undefined;
  transferHistoryOpened: undefined;
  substitutionConfirmed: undefined;
  matchCentreLink: { link_name: string };
  matchDetailsExpanded: undefined;
  matchDetailsCollapsed: undefined;
  howToPlayTextLink: { link_text: string };
  primaryNavigation: { link_name: string };
  playerSearch: undefined;
  playerProfileOpened: PlayerDetails;
  playerProfileDataToggle: { toggle_name: string };
  playerProfileFilter: { filter_name: string; filter_value: string };
  playerProfileMatchLink: { link_name: string };
  myTeamDataToggle: { toggle_name: string };
  resetPendingTransfers: undefined;
  rankHistoryOpened: undefined;
  leagueJoinedByCode: undefined;
  leagueLink: { link_name: string };
  leagueCreated: undefined;
  leagueDataToggle: { toggle_name: string };
  leagueSettingsUpdated: undefined;
  leaguePlayerRemoved: undefined;
  leagueInvite: { link_name: string };
  matchCentreFilter: { filter_name: string; filter_value: string };
  statsCentreDataToggle: { toggle_name: string };
  statsCentreFilter: { filter_name: string; filter_value: string };
}

type FantasyTrackingArguments<TKey extends FantasyEventContractKey> =
  FantasyEventDetailsByContract[TKey] extends undefined
    ? [details?: undefined]
    : [details: FantasyEventDetailsByContract[TKey]];

const hasOwn = (value: object, key: PropertyKey) =>
  Object.prototype.hasOwnProperty.call(value, key);

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const normalizeString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;

  const normalized = value.trim();
  if (!normalized || INVALID_STRING_VALUES.has(normalized.toLowerCase())) return undefined;

  return normalized;
};

const getContract = (contractKey: unknown): FantasyEventContract | undefined => {
  if (typeof contractKey !== 'string' || !hasOwn(FANTASY_EVENT_CONTRACTS, contractKey)) {
    return undefined;
  }

  return FANTASY_EVENT_CONTRACTS[contractKey as FantasyEventContractKey];
};

export const buildFantasyEventPayload = (
  contractKey: FantasyEventContractKey,
  details: unknown = {}
): FantasyPayloadBuildResult => {
  try {
    const contract = getContract(contractKey);
    if (!contract) return { ok: false, reason: 'unknown_contract' };
    if (!isPlainObject(details)) return { ok: false, reason: 'invalid_details' };

    const labelInputs = Object.values(contract.labelInterpolations ?? {});
    const approvedInputFields = new Set([...contract.approvedDetailFields, ...labelInputs]);
    const normalizedDetails: Record<string, string> = {};

    for (const [field, rawValue] of Object.entries(details)) {
      if (!approvedInputFields.has(field)) {
        return { ok: false, reason: 'unapproved_field', field };
      }

      const normalizedValue = normalizeString(rawValue);
      if (!normalizedValue) {
        return { ok: false, reason: 'invalid_field_value', field };
      }

      normalizedDetails[field] = normalizedValue;
    }

    const requiredFields = new Set([...contract.requiredDetailFields, ...labelInputs]);
    for (const field of requiredFields) {
      if (!normalizedDetails[field]) {
        return { ok: false, reason: 'missing_required_field', field };
      }
    }

    for (const [field, expectedValue] of Object.entries(contract.expectedDetailValues ?? {})) {
      if (normalizedDetails[field] !== expectedValue) {
        return { ok: false, reason: 'invalid_field_value', field };
      }
    }

    if (
      contract.approvedDetailFields.includes('formation') &&
      !FORMATION_VALUE.test(normalizedDetails.formation ?? '')
    ) {
      return { ok: false, reason: 'invalid_field_value', field: 'formation' };
    }

    let eventLabel = contract.eventLabel;
    for (const [placeholder, inputField] of Object.entries(contract.labelInterpolations ?? {})) {
      eventLabel = eventLabel.replaceAll(`{${placeholder}}`, normalizedDetails[inputField] ?? '');
    }

    if (UNRESOLVED_PLACEHOLDER.test(eventLabel)) {
      return { ok: false, reason: 'unresolved_label' };
    }

    const approvedDetails = Object.fromEntries(
      contract.approvedDetailFields.map(field => [field, normalizedDetails[field]])
    );

    return {
      ok: true,
      eventName: contract.eventName,
      payload: {
        section_reference: FANTASY_GAME_ID,
        dtfantasy_interaction: contract.eventAction,
        event_category: contract.eventCategory,
        event_action: contract.eventAction,
        event_label: eventLabel,
        ...approvedDetails,
      },
    };
  } catch {
    return { ok: false, reason: 'invalid_details' };
  }
};

/**
 * This boundary never throws. Callers must not use its result to control game behaviour.
 */
export const trackFantasyEvent = <TKey extends FantasyEventContractKey>(
  contractKey: TKey,
  ...[details]: FantasyTrackingArguments<TKey>
): FantasyTrackingResult => {
  try {
    const result = buildFantasyEventPayload(contractKey, details ?? {});
    if (!result.ok) return { status: 'skipped', reason: result.reason, field: result.field };

    return { status: trackTealiumEvent(result.eventName, result.payload) };
  } catch {
    return { status: 'skipped', reason: 'invalid_details' };
  }
};
